/**
 * Admin Orca Runner RPC definitions
 *
 * Operator-facing endpoint for the build-runner pool: flip an ONLINE runner
 * into DRAINING (the I18 operator drain). A DRAINING runner is no longer
 * ONLINE, so the scheduler's defer-on-no-ONLINE dispatch gate (I5) stops
 * handing it new work while its in-flight containers finish — after which the
 * daemon can be safely stopped/updated (e.g. an image rebuild) before going
 * OFFLINE.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OrcaRunnerStatus } from "@fm/db/models/OrcaRunner";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * CLA-398: one container a runner is currently reported as executing, projected
 * from its latest {@link ReportContainersPayload} snapshot. The container name
 * encodes `orca-{role}-{issueIdentifier}-{attempt}`, so the admin UI can map a
 * running container back to the task that owns it.
 *
 * @since 1.0.0
 */
export const OrcaRunnerRunningContainer = Schema.Struct({
  containerId: Schema.String,
  name: Schema.String,
  image: Schema.String,
  status: Schema.String,
  startedAt: Schema.DateTimeUtc,
});

/**
 * @since 1.0.0
 */
export type OrcaRunnerRunningContainer = Schema.Schema.Type<typeof OrcaRunnerRunningContainer>;

/**
 * CLA-398: operator-observability row for one build runner. Carries the pool
 * lifecycle state (status / capacity / heartbeat / connected timestamps) plus
 * the containers the runner last reported running. Deliberately omits the
 * runner's `runnerToken` hash — the pool list is read-only observability, never
 * a credential surface.
 *
 * @since 1.0.0
 */
export const OrcaRunnerSummary = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  status: OrcaRunnerStatus,
  capacity: Schema.Number,
  labels: Schema.Array(Schema.String),
  lastHeartbeatAt: Schema.NullOr(Schema.DateTimeUtc),
  connectedAt: Schema.NullOr(Schema.DateTimeUtc),
  // OL-61: the customer this runner is dedicated to, or `null` for a legacy row
  // that predates the tenancy backfill. Read-only observability — placement
  // matches this exactly with no unowned fallback, so an operator debugging a
  // parked dispatch needs to see it. Never a scope field: this group is
  // admin-only and stays unscoped.
  customerId: Schema.NullOr(Schema.String),
  runningContainers: Schema.Array(OrcaRunnerRunningContainer),
});

/**
 * @since 1.0.0
 */
export type OrcaRunnerSummary = Schema.Schema.Type<typeof OrcaRunnerSummary>;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input schema for draining a single runner by id.
 *
 * @since 1.0.0
 */
export const OrcaRunnerDrainInput = Schema.Struct({
  id: Schema.String,
});

/**
 * @since 1.0.0
 */
export type OrcaRunnerDrainInput = Schema.Schema.Type<typeof OrcaRunnerDrainInput>;

/**
 * INF-755: input schema for setting a runner's server-owned capacity live.
 * `capacity` is validated as a non-negative integer ({@link Schema.NonNegativeInt})
 * — a runner may be set to 0 to stop taking new work while staying ONLINE,
 * and a negative or fractional value is rejected at the wire boundary.
 *
 * @since 1.0.0
 */
export const OrcaRunnerSetCapacityInput = Schema.Struct({
  id: Schema.String,
  capacity: Schema.NonNegativeInt,
});

/**
 * @since 1.0.0
 */
export type OrcaRunnerSetCapacityInput = Schema.Schema.Type<typeof OrcaRunnerSetCapacityInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Runner RPC group.
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminOrcaRunnerRpcs extends RpcGroup.make(
  // CLA-398 operator observability: list the build-runner pool with its
  // online/capacity/heartbeat state and the containers each runner is currently
  // running. Read-only — a transient Redis snapshot read failure degrades to an
  // empty container list rather than failing the whole pool view.
  Rpc.make("admin.orcaRunner.list", {
    success: Schema.Array(OrcaRunnerSummary),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  // Operator drain: flip an ONLINE runner into DRAINING. Guarded on the ONLINE
  // state — a runner that is missing or already past ONLINE yields
  // RecordNotFoundError so the operator never silently re-drains a runner that
  // is not actively taking work.
  Rpc.make("admin.orcaRunner.drain", {
    payload: OrcaRunnerDrainInput,
    success: OrcaRunnerSummary,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // INF-755 operator set-capacity: `orca_runners.capacity` is server-owned (the
  // register handshake no longer writes it), so this is the live edit path.
  // Updates the row by id and the next dispatch selection reads the new value
  // via the pool — no daemon restart needed. A missing runner matches no row,
  // so the generic accessor surfaces RecordNotFoundError, mirroring drain.
  Rpc.make("admin.orcaRunner.setCapacity", {
    payload: OrcaRunnerSetCapacityInput,
    success: OrcaRunnerSummary,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
